import { Button, Col, Form, PaginationProps, Popover, Row } from 'antd';
import { useEffect, useRef, useState } from 'react';
import {
  LockFill,
  Mailbox,
  Pencil,
  Plus,
  ThreeDotsVertical,
  Trash,
  UnlockFill,
  XSquare,
} from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import {
  useCreateUser,
  useDeleteUser,
  useGetUser,
  useGetUserRoles,
  resendActivationEmail,
  useLockUnlockkUser,
} from '../../../api/hooks/useUserQuery';
import { ActionMenu } from '../../../components/common/ActionMenu';
import ModelComponent from '../../../components/common/ModelComponent';
import TableComponent from '../../../components/common/TableComponent';
import { FormButton } from '../../../components/form/FormBasic';
import ConfirmModelComponent, {
  ModelType,
} from '../../../components/model/ConfirmationModelComponent';
import { Configuration } from '../../../constants/configs';
import {
  REGEX_ALL_ALLOWED_SAFE_FILTER,
  REGEX_LETTERS_PUNCTUATION_SAFE_FILTER,
} from '../../../constants/regex';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { getServerErrorMsg } from '../../../helpers/errorHandler';
import { FetcherResponse } from '../../../helpers/fetcher';
import { customizeRequiredMark, renderField, resetFormFields } from '../../../helpers/formHelper';
import { getColorFormattedText } from '../../../helpers/utilityHelper';
import { BaseQuery } from '../../../types/query';
import { User, UserRegisterRequest, UserRole } from '../../../types/user';
import PermissionGuard from '../../../guard/permissionGuard';
import { PermissionEnum } from '../../../enums/PermissionEnum';
import { usePermissions } from '../../../hooks/usePermissions';
import { UsersStatusEnum } from '../../../enums/UserStatusEnum';
import UserManagementFilters from './UserManagementFilters';
import useDebounce from '../../../hooks/useDebounce';
import { useEditUser } from '../../../api/hooks/useUserQuery';
import { useUserContext } from '../../../contexts/UserContext';
import Tag from '../../../components/tag/Tag';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { RoleName } from '../../../enums/RoleNameEnm';

type UserTableRow = User & { key: string; name: string; status: number };

interface Filters {
  roleId: number | undefined | null;
  nameOrEmail: string;
}
interface UsersTableProps {
  activeTab: string;
}

const UsersTable = ({ activeTab }: UsersTableProps) => {
  const { t } = useTranslation(['userManagement', 'common']);
  const { errorNotification, successNotification } = useNotificationContext();
  const [isShowRegister, setIsShowRegister] = useState(false);
  const [isShowEditUser, setIsShowEditUser] = useState(false);
  const [isShowDeleteConfirmation, setIsShowDeleteConformation] = useState(false);
  const [isShowLockUserConfirmation, setIsShowLockUserConfirmation] = useState(false);
  const [isShowUnlockUserConfirmation, setIsShowUnlockUserConfirmation] = useState(false);
  const [isShowResendActivationModal, setIsShowResendActivationModal] = useState(false);
  const [userIdForActivation, setUserIdForActivation] = useState<string>('');
  const [initialFormValues, setInitialFormValues] = useState<UserTableRow | null>(null);
  const [form] = Form.useForm();
  const createUserMutation = useCreateUser();
  const deleteUserMutation = useDeleteUser();
  const editUserMutation = useEditUser();
  const lockUnlockUserMutation = useLockUnlockkUser();
  const userRoleGetMutation = useGetUserRoles();
  const userGetAllMutation = useGetUser();
  const resendActivation = resendActivationEmail();
  const [userTableRow, setUserTableRow] = useState<UserTableRow[]>();
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const initialLoadCompleted = useRef({} as any);
  const selectedUser = useRef<UserTableRow>({} as any);
  const [loading, setLoading] = useState(false);
  const { hasPermission } = usePermissions();
  const { isOnline } = useIsOnlineContext();

  const isHasRolePermission = hasPermission([
    PermissionEnum.USER_ROLE,
    PermissionEnum.VIEW_USER_ROLE,
    PermissionEnum.MANAGE_USER_ROLE,
  ]);

  const { getUserInfo } = useUserContext();
  const [auditView, setAuditView] = useState<{
    userId: string | null;
    isShow: boolean;
    name: string;
  }>();

  const [filters, setFilters] = useState<Filters>({
    roleId: null,
    nameOrEmail: '',
  });

  const debouncedNameOrEmail = useDebounce(filters.nameOrEmail, 500);

  useEffect(() => {
    if (!initialLoadCompleted.current) return;
    getUserDetails();
  }, [currentPage, pageSize, filters.roleId, debouncedNameOrEmail]);

  useEffect(() => {
    if (!isHasRolePermission || activeTab !== 'users') return;
    const load = async () => {
      await getUserRoles();
    };
    load();
  }, [activeTab]);

  useEffect(() => {
    getUserDetails();
    initialLoadCompleted.current = true;
  }, [setUserRoles]);

  const getUserDetails = () => {
    setLoading(true);
    const filterAnd: any[] = [];

    if (filters.roleId) {
      filterAnd.push({
        key: 'roleId',
        operation: '=',
        value: filters.roleId,
      });
    }

    const filterOr: any[] = [];

    if (filters.nameOrEmail) {
      const trimmedNameOrEmail = filters.nameOrEmail.trim();
      filterOr.push(
        { key: 'fullName', operation: 'ILIKE', value: `%${trimmedNameOrEmail}%` },
        { key: 'email', operation: 'ILIKE', value: `%${trimmedNameOrEmail}%` }
      );
    }

    const payload: BaseQuery = {
      page: currentPage,
      size: pageSize,
      filterAnd: filterAnd.length > 0 ? filterAnd : undefined,
      filterOr: filterOr.length > 0 ? filterOr : undefined,
      sort: {
        key: 'profileId',
        order: 'DESC',
      },
    };

    userGetAllMutation.mutate(payload, {
      onSuccess: (response: FetcherResponse) => {
        try {
          const rows: UserTableRow[] = response.data?.data.data.map((userData: any) => {
            const result: any = {
              id: userData.profileId,
              key: userData.id,
              name: userData.fullName,
              firstName: userData.firstName,
              lastName: userData.lastName,
              email: userData.email,
              organization: userData.organization,
              position: userData.position,
              department: userData.department,
              status: userData.status,
              roleName: userData.roleName,
            };
            // if (isHasRolePermission) {
            //   result['role'] = userRoles.find((roles) => roles.id === userData.roleId)?.name;
            // }
            return result;
          });

          setTotalRecords(response.data?.data.total || 0);
          setUserTableRow(rows);
        } catch (error: any) {
          setLoading(false);
          console.log('Error ', error);
        } finally {
          setLoading(false);
        }
      },
      onError(error) {
        console.log('error ', error);
        setLoading(false);
      },
    });
  };

  const getUserRoles = async () => {
    try {
      const result = await userRoleGetMutation.mutateAsync({
        page: 1,
        size: 10,
        allRecords: true,
      });
      const roles = result.data?.data.data || [];
      const sortedRoles = [...roles].sort((a, b) => {
        const nameA = t(a.name) || a.name;
        const nameB = t(b.name) || b.name;
        return nameA.localeCompare(nameB);
      });
      setUserRoles(sortedRoles);
    } catch (error) {}
  };

  const hasUserDataChanged = (
    values: Record<string, any>,
    initialValues: UserTableRow | null
  ): boolean => {
    if (!initialValues) {
      console.error(t('initialFormValuesNotSet'));
      return false;
    }

    if (values.role) {
      const role = userRoles.find((r: { id: any }) => r.id === values.role);
      if (role) {
        values.role = role.name;
      }
    }

    return Object.keys(values).some((key) => {
      const newValue = values[key];
      const oldValue = initialValues[key as keyof UserTableRow];
      if ((newValue ?? '') !== (oldValue ?? '')) {
        return true;
      }
      return false;
    });
  };

  const isOwnUserEdit = (email: string) => {
    const userEmail = getUserInfo('userEmail');
    return email === userEmail;
  };

  const handleEditSubmit = (values: any) => {
    Object.keys(values).forEach((key) => {
      if (typeof values[key] === 'string') {
        values[key] = values[key].trim();
      }
    });

    if (!hasUserDataChanged(values, initialFormValues)) {
      errorNotification(t('noChangesDetected'));
      return;
    }

    //changing rolename coming as value to role id for non changed values
    if (isHasRolePermission) {
      const role = userRoles.find((roles: { name: any }) => roles.name === values.role);
      if (role) {
        values.role = role.id;
      }
    }

    const updatedUserData = {
      userId: selectedUser.current.key?.trim(),
      ...values,
    };

    editUserMutation.mutate(updatedUserData, {
      onSuccess: () => {
        setIsShowEditUser(false);
        form.resetFields();
        getUserDetails();
        successNotification(t('messageUserEditCompleted'));
      },
      onError: (response) => {
        const errorMsg = getServerErrorMsg(response);
        errorNotification(errorMsg);
      },
    });
  };

  const handleSubmit = () => {
    const userSubmitData: UserRegisterRequest = {
      firstName: form.getFieldValue('firstName'),
      lastName: form.getFieldValue('lastName'),
      email: form.getFieldValue('email'),
      password: form.getFieldValue('password'),
      role: form.getFieldValue('role'),
      organization: form.getFieldValue('organization'),
      position: form.getFieldValue('position'),
      department: form.getFieldValue('department'),
    };

    createUserMutation.mutate(userSubmitData, {
      onSuccess: () => {
        setIsShowRegister(false);
        form.resetFields();
        getUserDetails();
        successNotification(t('messageUserRegisterCompleted'));
      },
      onError: (response) => {
        const errorMsg = getServerErrorMsg(response);
        errorNotification(errorMsg);
      },
    });
  };

  const handleCancel = () => {
    resetFormFields(form, Configuration.userRegisterForms);
    setIsShowRegister(false);
  };

  const handleCancelEdit = () => {
    resetFormFields(form, Configuration.userRegisterForms);
    setIsShowEditUser(false);
  };

  const showUserRegister = () => {
    setIsShowRegister(true);
  };

  const showUserEdit = (user: User & { status: number }) => {
    const updatedUser: UserTableRow = {
      ...user,
      key: user.key || user.id,
      name: `${user.firstName} ${user.lastName}`,
      status: user.status,
    };
    setInitialFormValues(updatedUser);
    selectedUser.current = updatedUser;
    Configuration.userRegisterForms.forEach((formItem: any) => {
      const key = formItem.fieldId as keyof User;
      if (formItem.fieldId === 'role') {
        const userRole = userRoles.find((role) => role.name === user['roleName']);
        if (userRole) {
          form.setFieldValue(formItem.fieldId, userRole.id);
        }
      } else {
        form.setFieldValue(formItem.fieldId, user[key]);
      }
    });
    setIsShowEditUser(true);
  };

  const validateInput = (label: string) => (rules: any, value: any) => {
    const field = t(label);
    const rawVal = (value ?? '').toString();
    const val = rawVal.trim();
    if (!rawVal) {
      if (['organization', 'position', 'department'].includes(label)) {
        return Promise.resolve();
      }
      return Promise.reject(`${field} ${t('require')}`);
    }

    if (!val) {
      return Promise.reject(t('fieldSpaceValidation', { field: field }));
    }

    if (!REGEX_ALL_ALLOWED_SAFE_FILTER.test(val)) {
      return Promise.reject(t('invalidInput'));
    }

    if (val.length > 80) {
      return Promise.reject(`${field} ${t('maxCharMessage')}`);
    }

    return Promise.resolve();
  };

  const validateName = (label: string) => (rules: any, value: any) => {
    const field = t(label);

    const rawVal = (value ?? '').toString();
    const val = rawVal.trim();
    if (!rawVal) {
      return Promise.reject(t('nameRequired', { field: field }));
    }

    if (!val) {
      return Promise.reject(t('fieldSpaceValidation', { field: field }));
    }

    if (!REGEX_LETTERS_PUNCTUATION_SAFE_FILTER.test(val)) {
      return Promise.reject(t('invalidInput'));
    }

    const startsWithLetter = /^[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(val);
    if (!startsWithLetter) {
      return Promise.reject(t('startWithLetter', { field: field }));
    }

    if (val?.length < 3) {
      return Promise.reject(`${field} ${t('minCharMessage')}`);
    }

    if (val?.length > 80) {
      return Promise.reject(`${field} ${t('maxCharMessage')}`);
    }

    return Promise.resolve();
  };

  const validateEmail = () => (_: any, value: any) => {
    const val = (value ?? '').toString().trim();

    if (!val) {
      return Promise.reject(t('emailRequired'));
    }

    const emailPattern = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailPattern.test(val)) {
      return Promise.reject(t('invalidInput'));
    }

    if (val.includes('..')) {
      return Promise.reject(t('invalidInput'));
    }

    if (/^[._-]|[._-]@/.test(val)) {
      return Promise.reject(t('invalidInput'));
    }

    return Promise.resolve();
  };

  const validateRole = (_: any, value: any) => {
    if (!value) {
      return Promise.reject(t('roleRequired'));
    }
    return Promise.resolve();
  };

  const getRegisterForm = () => {
    if (userRoles.length === 0) return null;

    const formFields = Configuration.userRegisterForms.map((f: any) => ({
      ...f,
      rules: f.rules ? [...f.rules] : [],
      values: f.values ? [...f.values] : undefined,
    }));

    return (
      <Form
        form={form}
        layout="vertical"
        requiredMark={customizeRequiredMark}
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Row gutter={[8, 8]}>
          {formFields.map((field: any) => {
            if (field.fieldId === 'role') {
              field.values =
                userRoles?.map((role: { id: any; name: any }) => ({
                  value: role.id,
                  label: role.name,
                })) || [];

              field.rules = [{ required: true, validator: validateRole }];
            }

            if (['department', 'organization', 'position'].includes(field.fieldId)) {
              const hasValidator = field.rules.some((r: any) => r && r.validator);
              if (!hasValidator) {
                field.rules = [...field.rules, { validator: validateInput(field.fieldId) }];
              }
            }

            if (['firstName', 'lastName'].includes(field.fieldId)) {
              const hasValidator = field.rules.some((r: any) => r && r.validator);
              if (!hasValidator) {
                field.rules = [...field.rules, { validator: validateName(field.fieldId) }];
              }
            }

            if (field.fieldId === 'email') {
              field.rules = [{ required: true, validator: validateEmail() }];
            }

            return (
              <Col span={24 / field.rowSize} key={field.fieldId}>
                {renderField(form, field, t)}
              </Col>
            );
          })}
        </Row>

        <Row className="justify-end gap-4">
          <Form.Item className="mt-5 flex justify-end">
            <Button onClick={handleCancel} className="btn-primary-border">
              {t('cancel')}
            </Button>
          </Form.Item>
          <Form.Item className="mt-5 flex justify-end">
            <FormButton
              type={'submit'}
              label={t('Register')}
              isLoading={createUserMutation.isPending}
            ></FormButton>
          </Form.Item>
        </Row>
      </Form>
    );
  };

  const getEditForm = () => {
    const formFields = Configuration.userRegisterForms
      .filter((f: any) => f.fieldId !== 'email')
      .map((f: any) => ({
        ...f,
        rules: f.rules ? [...f.rules] : [],
        values: f.values ? [...f.values] : undefined,
      }));

    formFields.forEach((field: any) => {
      if (field.fieldId === 'role') {
        field.values =
          userRoles?.map((role: { id: any; name: any }) => ({
            value: role.id,
            label: role.name,
          })) || [];
      }

      if (['department', 'organization', 'position'].includes(field.fieldId)) {
        const hasValidator = field.rules.some((r: any) => r && r.validator);
        if (!hasValidator) {
          field.rules = [...field.rules, { validator: validateInput(field.fieldId) }];
        }
      }

      if (['firstName', 'lastName'].includes(field.fieldId)) {
        const hasValidator = field.rules.some((r: any) => r && r.validator);
        if (!hasValidator) {
          field.rules = [...field.rules, { validator: validateName(field.fieldId) }];
        }
      }
    });

    return (
      <Form
        form={form}
        layout="vertical"
        requiredMark={customizeRequiredMark}
        onFinish={handleEditSubmit}
        autoComplete="off"
      >
        <Row gutter={[16, 16]}>
          {formFields.map((field: any) => {
            return (
              <Col span={24 / field.rowSize} key={field.fieldId}>
                {renderField(form, field, t)}
              </Col>
            );
          })}
        </Row>

        <Row className="justify-end gap-4">
          <Form.Item className="mt-5 flex justify-end">
            <Button onClick={handleCancelEdit} className="btn-primary-border">
              {t('cancel')}
            </Button>
          </Form.Item>
          <Form.Item className="mt-5 flex justify-end">
            <FormButton type={'submit'} label={t('save')} isLoading={editUserMutation.isPending} />
          </Form.Item>
        </Row>
      </Form>
    );
  };

  const registerCancelConfirmationHeaderContent = (action: string) => {
    return (
      <label className="text-primary-font-gray my-6 text-center text-xl font-bold">
        {t(action)}
      </label>
    );
  };

  const onCancelRequestCanceled = (type: string) => {
    if (type === 'delete') setIsShowDeleteConformation(false);
    else if (type === 'lock') setIsShowLockUserConfirmation(false);
    else if (type === 'unlock') setIsShowUnlockUserConfirmation(false);
  };

  const onPaginationChanged: PaginationProps['onChange'] = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const onDeleteUser = () => {
    deleteUserMutation.mutate(
      { id: selectedUser.current.key.toString(), versionId: '1.0' },
      {
        onSuccess: () => {
          successNotification(t('messageUserDeleted'));
          getUserDetails();
          setIsShowDeleteConformation(false);
        },
        onError: (response: any) => {
          errorNotification(response.message);
          setIsShowDeleteConformation(false);
        },
      }
    );
  };

  const onLockUnlockUser = (action: string, message: string) => {
    lockUnlockUserMutation.mutate(
      {
        userId: selectedUser.current.key,
        versionId: '1.0',
        action: action,
      },
      {
        onSuccess: () => {
          successNotification(t(message));
          getUserDetails();
        },
      }
    );
    setIsShowLockUserConfirmation(false);
    setIsShowUnlockUserConfirmation(false);
  };

  const dynamicMenuItems = (record: UserTableRow) => {
    let items: any[] = [];
    const mainActionFields = [
      ...(record.status !== UsersStatusEnum.BLOCKED &&
      record.status !== UsersStatusEnum.DELETED &&
      record.status !== UsersStatusEnum.INACTIVE &&
      record.email !== getUserInfo('userEmail')
        ? [
            {
              text: t('lock'),
              icon: <LockFill size={16} className="text-primary-blue-icon" />,
              click: () => {
                selectedUser.current = record;
                setIsShowLockUserConfirmation(true);
              },
            },
          ]
        : []),
      ...(record.status === UsersStatusEnum.BLOCKED
        ? [
            {
              text: t('unlock'),
              icon: <UnlockFill size={16} className="text-primary-blue-icon" />,
              click: () => {
                selectedUser.current = record;
                setIsShowUnlockUserConfirmation(true);
              },
            },
          ]
        : []),
    ];

    if (hasPermission([PermissionEnum.MANAGE_USER, PermissionEnum.USER])) {
      if (!isOwnUserEdit(record.email)) {
        mainActionFields.push({
          text: t('edit'),
          icon: <Pencil size={16} className="text-primary-blue-icon" />,
          click: () => {
            showUserEdit(record);
          },
        });
        if (record.status === UsersStatusEnum.INACTIVE) {
          mainActionFields.push({
            text: t('delete'),
            icon: <Trash size={16} className="text-primary-blue-icon" />,
            click: () => {
              selectedUser.current = record;
              setIsShowDeleteConformation(true);
            },
          });
        }
      }
    }

    const resendActivationLink = (id: string) => ({
      text: t('resendActivationLink'),
      icon: <Mailbox size={16} className="text-primary-blue-icon" />,
      click: () => {
        setUserIdForActivation(id);
        setIsShowResendActivationModal(true);
      },
    });

    if (hasPermission([PermissionEnum.MANAGE_USER, PermissionEnum.USER])) {
      items = [...mainActionFields];
    }
    if (
      hasPermission([PermissionEnum.USER, PermissionEnum.RESEND_ACTIVATION_USER]) &&
      record.status === UsersStatusEnum.INACTIVE &&
      record.email !== getUserInfo('userEmail')
    ) {
      items = [...items, resendActivationLink(record.key)];
    }

    /* --- user audit log removal --- */
    /* if (hasPermission([PermissionEnum.VIEW_AUDIT_LOGS, PermissionEnum.AUDIT_LOGS])) {
      items.push({
        text: t('userAuditLog'),
        icon: <Clipboard2Data size={16} className="text-primary-blue-icon" />,
        click: () => {
          setAuditView({
            userId: record.key,
            isShow: true,
            name: record.name,
          });
        },
      });
    } */
    return items;
  };

  const onResendActivation = () => {
    resendActivation.mutate(
      { userId: userIdForActivation },
      {
        onSuccess: () => {
          successNotification(t('resendActivationEmail'));
          setIsShowResendActivationModal(false);
        },
        onError: (response: any) => {
          errorNotification(response.message);
        },
      }
    );
  };

  const resendActivationModalFooterAction = {
    confimation: {
      label: t('confirm'),
      onClick: onResendActivation,
    },
    cancel: {
      label: t('cancel'),
      onClick: () => setIsShowResendActivationModal(false),
    },
  };

  const statusColors: Record<string, string> = {
    0: '#FFBC05',
    1: '#81C784',
    2: '#FF7A99',
    3: '#9E9E9E',
    4: '#BA68C8',
    5: '#64B5F6',
  };

  const columns = [
    {
      title: t('userId'),
      dataIndex: 'id',
      key: 'userId',
      with: 100,
    },
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      with: 200,
    },
    ...(isHasRolePermission
      ? [
          {
            title: t('role'),
            dataIndex: 'roleName',
            key: 'roleName',
            align: 'center' as const,
            render: (_: any, record: any) => {
              // console.log('------role----', record);
              return (
                <div className="flex flex-row justify-center">
                  <Tag
                    tagType={
                      record.roleName === RoleName.ADMIN
                        ? 'success'
                        : record.roleName === RoleName.INSPECTOR
                          ? 'caution'
                          : record.roleName === RoleName.ANALYST
                            ? 'warning'
                            : record.roleName === RoleName.AUDITOR
                              ? 'caution'
                              : record.roleName === RoleName.CITIZEN
                                ? 'neutral'
                                : 'neutral'
                    }
                  >
                    {t(record.roleName)}
                  </Tag>
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: t('email'),
      dataIndex: 'email',
      key: 'email',
      with: 100,
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (_: any, record: any) => {
        return (
          // <span
          //   className={classNames(
          //     'rounded-full border-[1px] p-1 px-2 text-xs font-semibold capitalize',
          //     { 'border-[#F5F5F5] bg-[#F5F5F5] text-[#171717]': record.status === 0 },
          //     { 'border-primary bg-primary text-white': record.status === 1 }
          //   )}
          // >
          //   {t(`status.${record.status}`)}
          // </span>
          <div className="flex flex-row justify-center">
            <Tag tagType={record.status === 1 ? 'success' : 'danger'} filled={true}>
              {t(`status.${record.status}`)}
            </Tag>
          </div>
        );
      },
    },
    {
      title: t('action'),
      dataIndex: 'action',
      key: 'action',
      with: 200,
      align: 'center',
      render: (_: any, record: UserTableRow) => {
        const menuItems = dynamicMenuItems(record);
        if (menuItems.length === 0) {
          return (
            <Row justify="center" style={{ cursor: 'not-allowed', opacity: 0.5 }}>
              <ThreeDotsVertical size={15} />
            </Row>
          );
        }
        return (
          <Popover
            content={<ActionMenu menuItems={menuItems} />}
            className="cursor-pointer"
            placement="bottom"
            trigger="hover"
            overlayStyle={{ zIndex: 800 }}
          >
            <Row justify="center">
              <ThreeDotsVertical size={15} />
            </Row>
          </Popover>
        );
      },
    },
  ];

  const handleFilterApply = () => {
    setCurrentPage(1);
  };

  return (
    <Row>
      <>
        <div className="flex w-full flex-col justify-between justify-stretch gap-4 md:flex-row md:items-center lg:flex-row">
          <UserManagementFilters
            filters={filters}
            setFilters={setFilters}
            onApply={handleFilterApply}
            userRoles={userRoles}
            isHasRolePermission={isHasRolePermission}
            setCurrentPage={setCurrentPage}
          />
          <PermissionGuard
            permission={[PermissionEnum.MANAGE_USER, PermissionEnum.USER]}
            fallback={<></>}
          >
            <PrimarySquareBtn handleOnClick={showUserRegister} disabled={!isOnline}>
              <div className="flex justify-center">
                <Plus className="h-6 w-6 text-white" />
                <p className="pt-1">{t('addUser')}</p>
              </div>
            </PrimarySquareBtn>

            {/* <FormButton
              onClick={showUserRegister}
              icon={<Plus></Plus>}
              label={''}
              isIconOnly
              type={'button'}
            ></FormButton> */}
          </PermissionGuard>
        </div>

        <div className="mt-6 w-full overflow-x-auto">
          <TableComponent
            tableData={userTableRow}
            pageSize={pageSize}
            currentPage={currentPage}
            totalItems={totalRecords}
            onPaginationChanged={onPaginationChanged}
            columns={columns}
            loading={loading}
            showHeader
          ></TableComponent>
        </div>
        <ModelComponent
          open={isShowRegister}
          title={t('registerNewUser')}
          footer={null}
          handleCancel={() => {
            resetFormFields(form, Configuration.userRegisterForms);
            setIsShowRegister(false);
          }}
          content={getRegisterForm()}
          disableBackgroundClick={true}
        ></ModelComponent>

        <ModelComponent
          open={isShowEditUser}
          title={t('editUser')}
          footer={null}
          handleCancel={() => {
            resetFormFields(form, Configuration.userRegisterForms);
            setIsShowEditUser(false);
          }}
          content={getEditForm()}
        ></ModelComponent>

        <ConfirmModelComponent
          isOpen={isShowResendActivationModal}
          headerContent={registerCancelConfirmationHeaderContent('confirmResendActivation')}
          modelType={ModelType.SUCCESS}
          content={t('confirmResendActivationMessage')}
          onClickedCancel={() => setIsShowResendActivationModal(false)}
          footerAction={resendActivationModalFooterAction}
          closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        />

        <ConfirmModelComponent
          isOpen={isShowDeleteConfirmation}
          headerContent={registerCancelConfirmationHeaderContent('confirmUserDelete')}
          content={t('areYouSureWantToDelete')}
          onClickedCancel={() => onCancelRequestCanceled('delete')}
          footerAction={{
            confimation: {
              label: t('deleteNow'),
              onClick: onDeleteUser,
              isLoading: deleteUserMutation.isPending,
              isDisabled: deleteUserMutation.isPending,
            },
            cancel: {
              label: t('back'),
              onClick: () => onCancelRequestCanceled('delete'),
            },
          }}
          closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        ></ConfirmModelComponent>

        <ConfirmModelComponent
          isOpen={isShowLockUserConfirmation}
          headerContent={registerCancelConfirmationHeaderContent('confirmUserLock')}
          content={t('areYouSureWantToLock')}
          onClickedCancel={() => onCancelRequestCanceled('lock')}
          footerAction={{
            confimation: {
              label: t('lock'),
              onClick: () => onLockUnlockUser('lock', 'messageUserLocked'),
            },
            cancel: {
              label: t('cancel'),
              onClick: () => onCancelRequestCanceled('lock'),
            },
          }}
          closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        ></ConfirmModelComponent>

        <ConfirmModelComponent
          isOpen={isShowUnlockUserConfirmation}
          headerContent={registerCancelConfirmationHeaderContent('confirmUserUnlock')}
          content={t('areYouSureWantToUnlock')}
          onClickedCancel={() => onCancelRequestCanceled('unlock')}
          footerAction={{
            confimation: {
              label: t('unlock'),
              onClick: () => onLockUnlockUser('unlock', 'messageUserUnlocked'),
            },
            cancel: {
              label: t('cancel'),
              onClick: () => onCancelRequestCanceled('unlock'),
            },
          }}
          closeIcon={<XSquare className="text-primary-font-red" size={20} />}
          modelType={ModelType.SUCCESS}
        ></ConfirmModelComponent>

        {/*  {auditView?.isShow && (
          <ModelComponent
            width={800}
            open={true}
            title={`${t('userAuditLog')} - ${auditView.name}`}
            footer={null}
            handleCancel={() => {
              setAuditView({ userId: null, isShow: false, name: '' });
            }}
            content={
              <>
                {auditView?.userId && (
                  <UserAuditLogComponent
                    translator={t}
                    userId={auditView.userId}
                  ></UserAuditLogComponent>
                )}
              </>
            }
          ></ModelComponent>
        )} */}
      </>
    </Row>
  );
};

export default UsersTable;
